import {
    AlertOutlined,
    CarryOutOutlined,
    CommentOutlined,
    DeleteFilled,
    HighlightOutlined,
    KeyOutlined,
    PlusOutlined,
    SortAscendingOutlined,
    WarningTwoTone,
} from "@ant-design/icons";
import type { FormInstance, FormListFieldData, TableProps } from "antd";
import { Button, Checkbox, Flex, Form, Input, Modal, Select, Tooltip } from "antd";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import fieldDataTypes from "common/data/fieldDataTypes";
import SlideMenuGuide, { useSlideMenuGuide } from "components/atoms/SlideMenuGuide";
import SlideoutMenu from "components/atoms/SlideoutMenu";
import DataTable from "components/molecules/DataTable";
import Annotations from "containers/Annotations";
import Comments from "containers/Comments";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import scrollIntoView from "scroll-into-view";
import CypressTestIds from "../../../cypress/CypressTestIds";
import ClassifiersTagField from "./ClassifiersTagField";
import "./style.css";

const FIELD_DATATYPE_OPTIONS = Object.keys(fieldDataTypes)
    .sort()
    .map((key) => ({ value: key, label: fieldDataTypes[key as keyof typeof fieldDataTypes].name }));

const SCHEMA_FIELD_TARGET_COLUMNS = [
    { name: "No." },
    { name: "Primary Key" },
    { name: "Sort Key" },
    { name: "Active" },
    { name: "Required" },
    { name: "Field Name" },
    { name: "Field Description" },
    { name: "Data Type" },
    { name: "Classifiers Tags" },
    { name: "Actions" },
];

interface SchemaModelFieldListProps {
    formRef: React.RefObject<FormInstance<ISchemaModel>>;
    fields: FormListFieldData[];
    userCommentsPermission?: IPermissionState;
    openFieldCommentId?: string;
    openFieldAnnotationId?: string;
    readOnly?: boolean;
    add?: (...args: any[]) => any;
    remove?: (...args: any[]) => any;
    formErrors?: any;
    getFieldRowLevelError?: (i: number) => string | number | null;
    getFormErrors: (name: string) => Object;
    onDataTypeChange: (dataTypeValue: string, field: FormListFieldData) => void;
    onFieldRowDelete: (removeAction: () => void, deleteContext?: { fieldIndex: number; fieldData?: ISchemaField | null }) => void;
    onFieldRowSelected?: (r: FormListFieldData) => void;
}

const SchemaModelFieldList = memo(
    ({
        formRef,
        fields,
        readOnly,
        add,
        remove,
        onFieldRowSelected,
        onDataTypeChange,
        getFormErrors,
        onFieldRowDelete,
        getFieldRowLevelError,
        userCommentsPermission,
        openFieldCommentId,
        openFieldAnnotationId,
    }: SchemaModelFieldListProps) => {
        const [scrollToLastRow, setScrollToLastRow] = useState(false);
        const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
        const [isCommentsModalVisible, setIsCommentsModalVisible] = useState(false);
        const [selectedFieldForComment, setSelectedFieldForComment] = useState<ISchemaField | null>(null);
        const [isAnnotationsModalVisible, setIsAnnotationsModalVisible] = useState(false);
        const [selectedFieldForAnnotation, setSelectedFieldForAnnotation] = useState<ISchemaField | null>(null);
        const [showMenuGuide, setShowMenuGuide] = useState(false);
        const { isReady, guidePostion } = useSlideMenuGuide("ant-table-header", "ant-table-row");

        useEffect(() => {
            setShowMenuGuide(isReady);
        }, [isReady]);

        useEffect(() => {
            if (scrollToLastRow === true) {
                setScrollToLastRow(false);
            }
            if (openFieldCommentId && formRef && formRef.current) {
                for (let f of fields) {
                    const actualFieldData = formRef.current.getFieldValue(["fields", f.name]);
                    if (actualFieldData && actualFieldData.fieldId === openFieldCommentId) {
                        setSelectedFieldForComment(actualFieldData);
                        setIsCommentsModalVisible(true);
                        break;
                    }
                }
            }
            if (openFieldAnnotationId && formRef && formRef.current) {
                for (let f of fields) {
                    const actualFieldData = formRef.current.getFieldValue(["fields", f.name]);
                    if (actualFieldData && actualFieldData.fieldId === openFieldAnnotationId) {
                        setSelectedFieldForAnnotation(actualFieldData);
                        setIsAnnotationsModalVisible(true);
                        break;
                    }
                }
            }
        }, [scrollToLastRow, fields, openFieldCommentId, openFieldAnnotationId, formRef]);

        const showCommentsModal = useCallback((fieldData: ISchemaField) => {
            setSelectedFieldForComment(fieldData);
            setIsCommentsModalVisible(true);
        }, []);

        const closeCommentsModal = () => {
            setIsCommentsModalVisible(false);
            setSelectedFieldForComment(null);
        };

        const showAnnotationsModal = useCallback((fieldData: ISchemaField) => {
            setSelectedFieldForAnnotation(fieldData);
            setIsAnnotationsModalVisible(true);
        }, []);

        const closeAnnotationsModal = () => {
            setIsAnnotationsModalVisible(false);
            setSelectedFieldForAnnotation(null);
        };

        const onClickRow = (record: FormListFieldData) => {
            return {
                onClick: () => {
                    setSelectedRowKeys([record.key]);
                    onFieldRowSelected && onFieldRowSelected(record);
                },
            };
        };

        const onIsPrimaryChange = useCallback(
            (e: any, field: FormListFieldData) => {
                if (e.target.checked && formRef.current) {
                    const allFields = formRef.current.getFieldValue(["fields"]);
                    for (let i = 0; i < allFields.length; i++) {
                        if (i !== field.name) {
                            allFields[i].isPrimaryKey = false;
                        } else {
                            allFields[i].isRequired = true;
                        }
                    }
                    formRef.current.setFieldsValue({
                        fields: allFields,
                    });
                }
            },
            [formRef],
        );

        const onIsRequiredChange = useCallback(
            (e: any, field: FormListFieldData) => {
                if (e.target.checked) {
                    const allFields = formRef.current?.getFieldValue(["fields"]) || [];
                    const current = allFields[field.name];
                    if (current && current.dataType === "string") {
                        const currentMin = current.min;
                        if (currentMin === 0) {
                            allFields[field.name].min = 1;
                            formRef.current?.setFieldsValue({
                                fields: allFields,
                            });
                        }
                    }
                    return;
                }

                const allFields = formRef.current?.getFieldValue(["fields"]) || [];
                if (allFields[field.name]) {
                    allFields[field.name].isPrimaryKey = false;
                    formRef.current?.setFieldsValue({
                        fields: allFields,
                    });
                }
            },
            [formRef],
        );

        const tableColumns = useMemo<TableProps<FormListFieldData>["columns"]>(
            () => [
                {
                    fixed: "left",
                    width: "40px",
                    key: "actions",
                    className: "overflow-visible !p-1",
                    render: (value: any, field: FormListFieldData) => {
                        const actualFieldData = formRef.current?.getFieldValue(["fields", field.name]);
                        const isSavedField = actualFieldData && actualFieldData.fieldId;
                        const totalRecordCount = Number((formRef.current as any)?.getFieldValue("totalRecordCount") || 0);
                        const isPrimaryKeyField = !!actualFieldData?.isPrimaryKey;
                        const isDeleteBlockedForPrimaryKey = totalRecordCount > 0 && isPrimaryKeyField;
                        const isDeleteDisabled = !!readOnly || isDeleteBlockedForPrimaryKey;
                        const canViewComments = userCommentsPermission?.canView;
                        return (
                            <SlideoutMenu>
                                <div className="flex flex-nowrap items-center justify-center">
                                    {canViewComments && (
                                        <>
                                            <Tooltip title={!isSavedField ? "Save the schema to add comments" : "Comments"}>
                                                <Button
                                                    type="link"
                                                    icon={<CommentOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isSavedField) {
                                                            showCommentsModal(actualFieldData);
                                                        }
                                                    }}
                                                    disabled={!isSavedField}
                                                />
                                            </Tooltip>
                                            <Tooltip title={!isSavedField ? "Save the schema to add annotations" : "Annotations"}>
                                                <Button
                                                    type="link"
                                                    icon={<HighlightOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isSavedField) {
                                                            showAnnotationsModal(actualFieldData);
                                                        }
                                                    }}
                                                    disabled={!isSavedField}
                                                    style={{ opacity: isSavedField ? 1 : 0.4 }}
                                                />
                                            </Tooltip>
                                            <div className="block h-6 w-0 border-r border-dashed border-gray-300" />
                                        </>
                                    )}
                                    {!!remove && (
                                        <Tooltip title={isDeleteBlockedForPrimaryKey ? "Cannot remove primary key field when schema has existing records" : "Delete"}>
                                            <Button
                                                danger
                                                type="text"
                                                icon={<DeleteFilled />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (isDeleteDisabled) {
                                                        return;
                                                    }
                                                    setSelectedRowKeys([]);
                                                    onFieldRowDelete && onFieldRowDelete(() => remove(field.name), { fieldIndex: field.name, fieldData: actualFieldData });
                                                }}
                                                disabled={isDeleteDisabled}
                                                data-testid={CypressTestIds.SCHEMA_FIELD_DELETE_BUTTON}
                                            />
                                        </Tooltip>
                                    )}
                                </div>
                            </SlideoutMenu>
                        );
                    },
                },
                {
                    width: "30px",
                    title: "No.",
                    key: "index",
                    render: (value: any, field: FormListFieldData, index: number) => {
                        if (getFieldRowLevelError) {
                            const fieldError = getFieldRowLevelError(index);
                            return (
                                <div className="flex gap-1">
                                    {fieldError ? (
                                        <Tooltip placement="topLeft" title={Object.values(fieldError).join("; ")}>
                                            <WarningTwoTone twoToneColor="#FF0000" />
                                        </Tooltip>
                                    ) : null}
                                    <Form.Item hidden name={[field.name, "fieldId"]}>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item hidden name={[field.name, "version"]}>
                                        <Input />
                                    </Form.Item>
                                    {index + 1}
                                </div>
                            );
                        }
                        return index + 1;
                    },
                },
                {
                    width: "60px",
                    dataIndex: "isPrimaryKey",
                    title: (
                        <Tooltip title="Is Primary Key?">
                            <Flex vertical className="items-center">
                                <KeyOutlined alt="Is Primary Key?" />
                                <span className="text-[7px]">Primary Key</span>
                            </Flex>
                        </Tooltip>
                    ),
                    ellipsis: { showTitle: false },
                    align: "center",
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    return (
                                        prevValues.totalRecordCount !== currentValues.totalRecordCount ||
                                        prevValues.fields?.[field.name]?.isPrimaryKey !== currentValues.fields?.[field.name]?.isPrimaryKey
                                    );
                                }}
                            >
                                {({ getFieldValue }) => {
                                    const totalRecordCount = getFieldValue("totalRecordCount") || 0;
                                    const isPrimaryKeyDisabled = readOnly || totalRecordCount > 0;
                                    return (
                                        <Tooltip title={totalRecordCount > 0 ? "Cannot modify primary key as the schema model has existing records" : ""}>
                                            <span className={isPrimaryKeyDisabled ? "inline-block cursor-not-allowed" : ""}>
                                                <Form.Item
                                                    {...otherProps}
                                                    key={key}
                                                    name={[field.name, "isPrimaryKey"]}
                                                    valuePropName="checked"
                                                    initialValue={false}
                                                    className="mb-0"
                                                >
                                                    <Checkbox
                                                        className={isPrimaryKeyDisabled ? "pointer-events-none" : ""}
                                                        disabled={isPrimaryKeyDisabled}
                                                        onChange={(e) => onIsPrimaryChange(e, field)}
                                                        data-testid={CypressTestIds.SCHEMA_FIELD_ISPRIMARY_CHECKBOX}
                                                    />
                                                </Form.Item>
                                            </span>
                                        </Tooltip>
                                    );
                                }}
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "60px",
                    dataIndex: "isSortKey",
                    title: (
                        <Tooltip title="Is Sort Key?">
                            <Flex vertical className="items-center">
                                <SortAscendingOutlined alt="Is Sort Key?" />
                                <span className="text-[7px]">Sort Key</span>
                            </Flex>
                        </Tooltip>
                    ),
                    ellipsis: { showTitle: false },
                    align: "center",
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item {...otherProps} key={key} name={[field.name, "isSortKey"]} valuePropName="checked" initialValue={false} className="mb-0">
                                <Checkbox disabled={readOnly} data-testid={CypressTestIds.SCHEMA_FIELD_ISSORTKEY_CHECKBOX} />
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "60px",
                    dataIndex: "active",
                    title: (
                        <Tooltip title="Is Active?">
                            <Flex vertical className="items-center">
                                <CarryOutOutlined alt="Is Active?" />
                                <span className="text-[7px]">Active</span>
                            </Flex>
                        </Tooltip>
                    ),
                    ellipsis: { showTitle: false },
                    align: "center",
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item {...otherProps} key={key} name={[field.name, "active"]} valuePropName="checked" initialValue={true} className="mb-0">
                                <Checkbox disabled={readOnly} data-testid={CypressTestIds.SCHEMA_FIELD_ISACTIVE_CHECKBOX} />
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "60px",
                    dataIndex: "isRequired",
                    title: (
                        <Tooltip title="Is Required?">
                            <Flex vertical className="items-center">
                                <AlertOutlined alt="Is Required?" />
                                <span className="text-[7px]">Required</span>
                            </Flex>
                        </Tooltip>
                    ),
                    ellipsis: { showTitle: false },
                    align: "center",
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    return (
                                        prevValues.fields?.[field.name]?.isPrimaryKey !== currentValues.fields?.[field.name]?.isPrimaryKey ||
                                        prevValues.fields?.[field.name]?.isRequired !== currentValues.fields?.[field.name]?.isRequired
                                    );
                                }}
                            >
                                {({ getFieldValue }) => {
                                    const isPrimaryKey = getFieldValue(["fields", field.name, "isPrimaryKey"]);
                                    const disabled = readOnly || isPrimaryKey;
                                    return (
                                        <Tooltip title={isPrimaryKey ? "Primary key fields must be required" : ""}>
                                            <span className={disabled ? "inline-block cursor-not-allowed" : ""}>
                                                <Form.Item
                                                    {...otherProps}
                                                    key={key}
                                                    name={[field.name, "isRequired"]}
                                                    valuePropName="checked"
                                                    initialValue={false}
                                                    className="mb-0"
                                                >
                                                    <Checkbox
                                                        className={disabled ? "pointer-events-none" : ""}
                                                        disabled={disabled}
                                                        onChange={(e) => onIsRequiredChange(e, field)}
                                                        data-testid={CypressTestIds.SCHEMA_FIELD_ISREQUIRED_CHECKBOX}
                                                    />
                                                </Form.Item>
                                            </span>
                                        </Tooltip>
                                    );
                                }}
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "200px",
                    dataIndex: "name",
                    title: "Field Name",
                    ellipsis: { showTitle: false },
                    className: "align-top",
                    render: (value: any, field: FormListFieldData, index: number) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    return prevValues.fields?.[field.name]?.isPrimaryKey !== currentValues.fields?.[field.name]?.isPrimaryKey;
                                }}
                            >
                                {({ getFieldValue }) => {
                                    const isPrimaryKey = getFieldValue(["fields", field.name, "isPrimaryKey"]);
                                    return (
                                        <Form.Item
                                            {...otherProps}
                                            key={key}
                                            name={[field.name, "name"]}
                                            validateFirst={true}
                                            rules={nameRules}
                                            {...getFormErrors(`fields.${field.name}.name`)}
                                            className="mb-0"
                                        >
                                            <Input
                                                readOnly={readOnly}
                                                ref={(element) => element && scrollToLastRow && index === fields.length - 1 && element.input && scrollIntoView(element.input)}
                                                data-testid={CypressTestIds.SCHEMA_FIELD_NAME_INPUT}
                                                suffix={
                                                    isPrimaryKey ? (
                                                        <Tooltip title="This field is the Primary Key">
                                                            <KeyOutlined className="text-yellow-500" alt="Primary Key" />
                                                        </Tooltip>
                                                    ) : null
                                                }
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "200px",
                    dataIndex: "description",
                    title: "Field Description",
                    ellipsis: { showTitle: false },
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item
                                {...otherProps}
                                name={[field.name, "description"]}
                                rules={descriptionRules}
                                {...getFormErrors(`fields.${field.name}.description`)}
                                className="mb-0"
                            >
                                <Input readOnly={readOnly} data-testid={CypressTestIds.SCHEMA_FIELD_DESCRIPTION_INPUT} />
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "200px",
                    dataIndex: "dataType",
                    title: "Data Type",
                    ellipsis: { showTitle: false },
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        const { key, ...otherProps } = field;
                        return (
                            <Form.Item {...otherProps} key={key} name={[field.name, "dataType"]} initialValue="string" className="mb-0">
                                <Select
                                    showSearch={{
                                        optionFilterProp: "label",
                                        filterOption: (input, option) => !!option && !!option.label && option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0,
                                    }}
                                    placeholder="Search to Select"
                                    onChange={(e) => onDataTypeChange && onDataTypeChange(e, field)}
                                    {...(() => (readOnly ? { open: false } : {}))()}
                                    data-testid={CypressTestIds.SCHEMA_FIELD_DATATYPE_SELECT}
                                    options={FIELD_DATATYPE_OPTIONS}
                                />
                            </Form.Item>
                        );
                    },
                },
                {
                    width: "150px",
                    dataIndex: "classifiers",
                    title: "Classifiers Tags",
                    ellipsis: { showTitle: false },
                    className: "align-top",
                    render: (value: any, field: FormListFieldData) => {
                        return (
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    return prevValues.fields?.[field.name]?.classifiers !== currentValues.fields?.[field.name]?.classifiers;
                                }}
                            >
                                {({ getFieldValue }) => {
                                    const classifiers = getFieldValue(["fields", field.name, "classifiers"]);
                                    const fieldId = getFieldValue(["fields", field.name, "fieldId"]);
                                    return classifiers ? <ClassifiersTagField fieldId={fieldId} value={classifiers} /> : <></>;
                                }}
                            </Form.Item>
                        );
                    },
                },
            ],
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                formRef,
                readOnly,
                remove,
                onDataTypeChange,
                getFormErrors,
                getFieldRowLevelError,
                userCommentsPermission,
                fields,
                scrollToLastRow,
                showCommentsModal,
                showAnnotationsModal,
                onIsPrimaryChange,
                onIsRequiredChange,
                onFieldRowDelete,
            ],
        );

        return (
            <>
                <Modal
                    title={`Comments for Field: "${selectedFieldForComment?.name}"`}
                    open={isCommentsModalVisible}
                    onCancel={closeCommentsModal}
                    footer={null}
                    width={800}
                    styles={{
                        body: { height: "60vh", overflow: "auto" },
                    }}
                >
                    {selectedFieldForComment?.fieldId && (
                        <Comments key={selectedFieldForComment.fieldId} recordId={selectedFieldForComment.fieldId} permission={userCommentsPermission} recordType="schemafield" />
                    )}
                </Modal>
                <Modal
                    title={`Annotations for Field: "${selectedFieldForAnnotation?.name}"`}
                    open={isAnnotationsModalVisible}
                    onCancel={closeAnnotationsModal}
                    footer={null}
                    width={900}
                    styles={{
                        body: { height: "70vh", overflowY: "auto", overflowX: "hidden" },
                    }}
                >
                    {selectedFieldForAnnotation?.fieldId && (
                        <Annotations
                            key={`${selectedFieldForAnnotation.fieldId}-annotations`}
                            recordId={selectedFieldForAnnotation.fieldId}
                            recordType="schemaField"
                            permission={userCommentsPermission}
                            fields={SCHEMA_FIELD_TARGET_COLUMNS}
                        />
                    )}
                </Modal>

                <DataTable<FormListFieldData>
                    showFooter={add ? true : false}
                    footer={
                        <Form.Item className="mb-0 mt-4 w-full flex-1">
                            <Button
                                block
                                onClick={() => {
                                    setScrollToLastRow(true);
                                    add && add();
                                }}
                                disabled={readOnly}
                                data-testid={CypressTestIds.SCHEMA_FIELD_ADD_BUTTON}
                            >
                                <PlusOutlined />
                                {fields.length === 0 ? "Add field" : "Add another field"}
                            </Button>
                        </Form.Item>
                    }
                    dataTestId={CypressTestIds.SCHEMA_FIELD_TABLE}
                    hideSelectionColumn
                    fixedHeader
                    tableProps={{
                        size: "small",
                        dataSource: fields,
                        pagination: false,
                        onRow: onClickRow,
                        columns: tableColumns,
                        rowSelection: {
                            selectedRowKeys: selectedRowKeys,
                            type: "radio",
                        },
                        className: "schema-model-field-table",
                        rowClassName: (field, index) => {
                            if (getFieldRowLevelError) {
                                let fieldError = getFieldRowLevelError(index);
                                if (fieldError) {
                                    return "error-row";
                                }
                            }
                            return "";
                        },
                    }}
                >
                    {fields && fields.length > 0 && <SlideMenuGuide open={showMenuGuide} position={guidePostion} onClose={() => setShowMenuGuide(false)} />}
                </DataTable>
            </>
        );
    },
    (prev: SchemaModelFieldListProps, next: SchemaModelFieldListProps) => {
        if (prev.fields.length !== next.fields.length || prev.fields.some((f, i) => f.key !== next.fields[i].key)) {
            return false;
        }

        return (
            prev.formRef === next.formRef &&
            prev.readOnly === next.readOnly &&
            prev.add === next.add &&
            prev.remove === next.remove &&
            prev.onFieldRowSelected === next.onFieldRowSelected &&
            prev.onDataTypeChange === next.onDataTypeChange &&
            prev.formErrors === next.formErrors &&
            prev.getFormErrors === next.getFormErrors &&
            prev.onFieldRowDelete === next.onFieldRowDelete &&
            prev.getFieldRowLevelError === next.getFieldRowLevelError &&
            prev.userCommentsPermission === next.userCommentsPermission &&
            prev.openFieldCommentId === next.openFieldCommentId &&
            prev.openFieldAnnotationId === next.openFieldAnnotationId
        );
    },
);

export default SchemaModelFieldList;
